import { createFileRoute } from "@tanstack/react-router"
import * as React from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Th, Td, fmt, fmtDate, fmtDateTime } from "@/components/data-table"
import { Trash2 } from "lucide-react"

export const Route = createFileRoute("/_protected/historial")({
  component: HistorialPage,
})

type Upload = {
  uploadFileName: string
  uploadedAt: number
  totalZonas: number
  totalSesiones: number
  totalUsuarios: number
}

type ZonaResumen = {
  item: string
  municipio: string | null
  nombre: string | null
  totalSesiones: number
  totalUsuarios: number
}

type DetalleRow = {
  id: number
  item: string
  tipoServicio: string | null
  municipio: string | null
  nombre: string | null
  fechaInicio: number | null
  sesiones: number
  usuarios: number
}

/* ─── Nivel 3: detalle por día de una zona ─── */
function DetalleZona({
  file, uploadedAt, zona, onBack,
}: { file: string; uploadedAt: number; zona: ZonaResumen; onBack: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["pc-detalle", file, uploadedAt, zona.item],
    queryFn: async () => {
      const p = new URLSearchParams({ file, uploadedAt: String(uploadedAt), zona: zona.item })
      const res = await fetch(`/api/portal-cautivo/uploads/detalle?${p}`, { credentials: "include" })
      return res.json() as Promise<DetalleRow[]>
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack}>← Volver a zonas</Button>
        <div>
          <span className="font-semibold text-base">{zona.item}</span>
          {zona.municipio && <span className="text-muted-foreground text-sm ml-2">— {zona.municipio}</span>}
          {zona.nombre && <span className="text-muted-foreground text-sm ml-1">/ {zona.nombre}</span>}
        </div>
      </div>

      {/* totales de la zona */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white p-3 rounded shadow text-center">
          <p className="text-xs text-muted-foreground">Total Sesiones</p>
          <p className="text-2xl font-bold">{fmt(zona.totalSesiones)}</p>
        </div>
        <div className="bg-white p-3 rounded shadow text-center">
          <p className="text-xs text-muted-foreground">Total Usuarios</p>
          <p className="text-2xl font-bold">{fmt(zona.totalUsuarios)}</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Detalle por día</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Cargando...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Fecha</Th>
                    <Th>Tipo Servicio</Th>
                    <Th>Municipio</Th>
                    <Th>Nombre</Th>
                    <Th right>Sesiones</Th>
                    <Th right>Usuarios</Th>
                  </tr>
                </thead>
                <tbody>
                  {data?.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50">
                      <Td mono>{fmtDate(row.fechaInicio)}</Td>
                      <Td>{row.tipoServicio ?? "—"}</Td>
                      <Td>{row.municipio ?? "—"}</Td>
                      <Td>{row.nombre ?? "—"}</Td>
                      <Td right>{fmt(row.sesiones ?? 0)}</Td>
                      <Td right>{fmt(row.usuarios ?? 0)}</Td>
                    </tr>
                  ))}
                  {data?.length === 0 && (
                    <tr><td colSpan={6} className="p-4 text-sm text-center text-muted-foreground">Sin registros</td></tr>
                  )}
                </tbody>
                {data && data.length > 0 && (
                  <tfoot>
                    <tr className="bg-muted font-semibold">
                      <td colSpan={4} className="px-3 py-2 text-sm text-right">Total</td>
                      <Td right>{fmt(data.reduce((a, r) => a + (r.sesiones ?? 0), 0))}</Td>
                      <Td right>{fmt(data.reduce((a, r) => a + (r.usuarios ?? 0), 0))}</Td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/* ─── Nivel 2: zonas de un archivo ─── */
function ZonasUpload({
  upload, onBack,
}: { upload: Upload; onBack: () => void }) {
  const [zonaSeleccionada, setZonaSeleccionada] = React.useState<ZonaResumen | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["pc-upload-zonas", upload.uploadFileName, upload.uploadedAt],
    queryFn: async () => {
      const p = new URLSearchParams({
        file: upload.uploadFileName,
        uploadedAt: String(upload.uploadedAt),
      })
      const res = await fetch(`/api/portal-cautivo/uploads/zonas?${p}`, { credentials: "include" })
      return res.json() as Promise<ZonaResumen[]>
    },
  })

  if (zonaSeleccionada) {
    return (
      <DetalleZona
        file={upload.uploadFileName}
        uploadedAt={upload.uploadedAt}
        zona={zonaSeleccionada}
        onBack={() => setZonaSeleccionada(null)}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack}>← Volver a archivos</Button>
        <div>
          <span className="font-semibold text-base">{upload.uploadFileName}</span>
          <span className="text-muted-foreground text-sm ml-2">subido el {fmtDateTime(upload.uploadedAt)}</span>
        </div>
      </div>

      {/* totales del archivo */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white p-3 rounded shadow text-center">
          <p className="text-xs text-muted-foreground">Zonas</p>
          <p className="text-2xl font-bold">{fmt(upload.totalZonas)}</p>
        </div>
        <div className="bg-white p-3 rounded shadow text-center">
          <p className="text-xs text-muted-foreground">Total Sesiones</p>
          <p className="text-2xl font-bold">{fmt(upload.totalSesiones)}</p>
        </div>
        <div className="bg-white p-3 rounded shadow text-center">
          <p className="text-xs text-muted-foreground">Total Usuarios</p>
          <p className="text-2xl font-bold">{fmt(upload.totalUsuarios)}</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Zonas — haz clic en una zona para ver el detalle diario</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Cargando...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Zona</Th>
                    <Th>Municipio</Th>
                    <Th>Nombre</Th>
                    <Th right>Sesiones</Th>
                    <Th right>Usuarios</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {data?.map((z) => (
                    <tr
                      key={z.item}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => setZonaSeleccionada(z)}
                    >
                      <Td mono>{z.item}</Td>
                      <Td>{z.municipio ?? "—"}</Td>
                      <Td>{z.nombre ?? "—"}</Td>
                      <Td right>{fmt(z.totalSesiones)}</Td>
                      <Td right>{fmt(z.totalUsuarios)}</Td>
                      <td className="px-3 py-2 text-sm text-primary text-right">Ver detalle →</td>
                    </tr>
                  ))}
                  {data?.length === 0 && (
                    <tr><td colSpan={6} className="p-4 text-sm text-center text-muted-foreground">Sin zonas</td></tr>
                  )}
                </tbody>
                {data && data.length > 0 && (
                  <tfoot>
                    <tr className="bg-muted font-semibold">
                      <td colSpan={3} className="px-3 py-2 text-sm text-right">Total</td>
                      <Td right>{fmt(data.reduce((a, z) => a + z.totalSesiones, 0))}</Td>
                      <Td right>{fmt(data.reduce((a, z) => a + z.totalUsuarios, 0))}</Td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/* ─── Nivel 1: lista de archivos subidos ─── */
function HistorialPage() {
  const [uploadSeleccionado, setUploadSeleccionado] = React.useState<Upload | null>(null)
  const [deletingKey, setDeletingKey] = React.useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["pc-uploads"],
    queryFn: async () => {
      const res = await fetch("/api/portal-cautivo/uploads", { credentials: "include" })
      return res.json() as Promise<Upload[]>
    },
  })

  const handleDelete = async (u: Upload, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm(`¿Eliminar el archivo "${u.uploadFileName}"?\nEsto borrará todos sus datos y no se puede deshacer.`)) return
    const key = `${u.uploadFileName}-${u.uploadedAt}`
    setDeletingKey(key)
    try {
      const params = new URLSearchParams({ file: u.uploadFileName, uploadedAt: String(u.uploadedAt) })
      await fetch(`/api/portal-cautivo/uploads?${params}`, { method: "DELETE", credentials: "include" })
      queryClient.invalidateQueries({ queryKey: ["pc-uploads"] })
    } finally {
      setDeletingKey(null)
    }
  }

  if (uploadSeleccionado) {
    return (
      <div className="p-6 space-y-6 bg-slate-100 min-h-screen">
        <h1 className="text-2xl font-bold">Historial de Cargas</h1>
        <ZonasUpload upload={uploadSeleccionado} onBack={() => setUploadSeleccionado(null)} />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 bg-slate-100 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Historial de Cargas</h1>
          <p className="text-sm text-gray-500">Archivos Excel subidos al sistema. Haz clic en uno para ver su detalle.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Actualizar</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Cargando...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Archivo</Th>
                    <Th>Fecha de carga</Th>
                    <Th right>Zonas</Th>
                    <Th right>Sesiones</Th>
                    <Th right>Usuarios</Th>
                    <Th></Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {data?.map((u) => {
                    const key = `${u.uploadFileName}-${u.uploadedAt}`
                    const isDeleting = deletingKey === key
                    return (
                      <tr
                        key={key}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setUploadSeleccionado(u)}
                      >
                        <Td>{u.uploadFileName}</Td>
                        <Td>{fmtDateTime(u.uploadedAt)}</Td>
                        <Td right>{fmt(u.totalZonas)}</Td>
                        <Td right>{fmt(u.totalSesiones)}</Td>
                        <Td right>{fmt(u.totalUsuarios)}</Td>
                        <td className="px-3 py-2 text-sm text-primary text-right">Ver detalle →</td>
                        <td className="px-2 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isDeleting}
                            onClick={(e) => handleDelete(u, e)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                  {data?.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-6 text-sm text-center text-muted-foreground">
                        No hay archivos subidos aún. Ve a <strong>Subir Sesiones</strong> para cargar el primero.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
